import React, { useState, useEffect, useCallback } from "react";
import { FaRegEdit, FaTrashAlt, FaMoon } from "react-icons/fa";
import { apiFetch } from "./utils/api";
import dnPopup from "./utils/dnPopup";
import logo_eagle from "./assets/img/logo_eagle.png";
import ScrollSelect from "./ScrollSelect";
import "./Shift.css";

// "HH:MM" or "HH:MM:SS" -> minutes since midnight
const parseHM = (t) => {
    if (!t) return null;
    const [h, m] = String(t).split(":").map(Number);
    return h * 60 + m;
};
const toHM = (t) => (t ? String(t).slice(0, 5) : ""); // strip seconds for <input type=time>

// Overnight-aware duration in minutes (end <= start crosses midnight)
const durationMins = (start, end) => {
    const s = parseHM(start), e = parseHM(end);
    if (s == null || e == null) return null;
    let mins = e - s;
    if (mins <= 0) mins += 24 * 60;
    return mins;
};

// minutes -> "8h 30m"
const fmtDuration = (mins) => {
    if (mins == null) return "—";
    const h = Math.floor(mins / 60), m = mins % 60;
    return m ? `${h}h ${m}m` : `${h}h`;
};

// "14:30" -> "2:30 PM"
const to12h = (t) => {
    const mins = parseHM(t);
    if (mins == null) return "—";
    let h = Math.floor(mins / 60);
    const m = mins % 60;
    const ap = h >= 12 ? "PM" : "AM";
    h = h % 12 || 12;
    return `${h}:${String(m).padStart(2, "0")} ${ap}`;
};

// add whole hours to "HH:MM", wrapping past midnight
const addHours = (t, hrs) => {
    const mins = parseHM(t);
    if (mins == null) return "";
    const total = (mins + hrs * 60) % (24 * 60);
    return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
};

const WARN_MINS = 10 * 60; // > 10h → warn but allow
const MAX_MINS = 12 * 60;  // > 12h → block

const HOURS_24 = Array.from({ length: 24 }, (_, i) => i);   // 0..23
const HOURS_12 = Array.from({ length: 12 }, (_, i) => i + 1); // 1..12
const MINUTES = Array.from({ length: 12 }, (_, i) => i * 5);  // 0,5,..55

const opt = (v, label) => ({ value: v, label });
const HH24_OPTS = HOURS_24.map((h) => opt(h, String(h).padStart(2, "0")));
const HH12_OPTS = HOURS_12.map((h) => opt(h, String(h)));
const MM_OPTS = MINUTES.map((m) => opt(m, String(m).padStart(2, "0")));
const AMPM_OPTS = [opt("AM", "AM"), opt("PM", "PM")];

// A friendly time picker. The value is always a 24h "HH:MM" string (what the
// backend stores); the UI can present it in 12h (AM/PM) or 24h form.
const TimeSelect = ({ value, onChange, format }) => {
    const [hh, mm] = value ? value.split(":").map(Number) : [null, null];
    const emit = (h24, m) => onChange(`${String(h24).padStart(2, "0")}:${String(m).padStart(2, "0")}`);

    if (format === "24h") {
        return (
            <div className="shift-time-select">
                <ScrollSelect value={hh ?? ""} options={HH24_OPTS} placeholder="HH"
                    onChange={(v) => emit(Number(v), mm ?? 0)} />
                <span className="shift-time-colon">:</span>
                <ScrollSelect value={mm ?? ""} options={MM_OPTS} placeholder="MM"
                    onChange={(v) => emit(hh ?? 0, Number(v))} />
            </div>
        );
    }

    // 12h
    const h12 = hh == null ? "" : (hh % 12 || 12);
    const ampm = hh == null ? "AM" : (hh >= 12 ? "PM" : "AM");
    const build = (nh12, nm, nap) => {
        const base = (nh12 === "" ? 12 : Number(nh12)) % 12;
        emit(nap === "PM" ? base + 12 : base, nm ?? 0);
    };
    return (
        <div className="shift-time-select">
            <ScrollSelect value={h12} options={HH12_OPTS} placeholder="HH"
                onChange={(v) => build(v, mm ?? 0, ampm)} />
            <span className="shift-time-colon">:</span>
            <ScrollSelect value={mm ?? ""} options={MM_OPTS} placeholder="MM"
                onChange={(v) => build(h12, Number(v), ampm)} />
            <ScrollSelect value={ampm} options={AMPM_OPTS} placeholder="AM"
                onChange={(v) => build(h12, mm ?? 0, v)} />
        </div>
    );
};

const Shift = ({ navSize }) => {
    const [shifts, setShifts] = useState([]);
    const [loading, setLoading] = useState(true);

    const [editId, setEditId] = useState(null);
    const [name, setName] = useState("");
    const [start, setStart] = useState("");
    const [end, setEnd] = useState("");
    const [saving, setSaving] = useState(false);
    const [timeFormat, setTimeFormat] = useState("12h"); // "12h" | "24h" — display only
    const [endEdited, setEndEdited] = useState(false);   // stop auto-suggest once End is touched

    const fetchShifts = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiFetch("/api/shift/list", { credentials: "include" });
            const data = await res.json();
            setShifts(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("Error fetching shifts:", err);
            setShifts([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchShifts(); }, [fetchShifts]);

    // When start changes, keep suggesting an 8h end — UNTIL the admin edits the
    // end themselves. This also self-corrects a premature suggestion (e.g. if
    // they pick the minute before the hour): the end re-tracks the real start.
    const onStartChange = (v) => {
        setStart(v);
        if (v && !endEdited) setEnd(addHours(v, 8));
    };

    // Editing the end stops the auto-suggest so the chosen value sticks.
    const onEndChange = (v) => {
        setEnd(v);
        setEndEdited(true);
    };

    const resetForm = () => { setEditId(null); setName(""); setStart(""); setEnd(""); setEndEdited(false); };

    const mins = durationMins(start, end);

    const save = async () => {
        if (!name.trim()) return dnPopup.warning("Required", "Please enter a shift name.");
        if (!start || !end) return dnPopup.warning("Required", "Please enter both start and end time.");
        if (mins == null) return dnPopup.warning("Invalid time", "Please enter valid start and end times.");
        if (parseHM(start) === parseHM(end)) {
            return dnPopup.warning("Invalid time", "Start and end time cannot be the same.");
        }
        if (mins > MAX_MINS) {
            return dnPopup.error("Shift too long", "A shift cannot be longer than 12 hours. Please adjust the times.");
        }
        if (mins > WARN_MINS) {
            const ok = await dnPopup.confirm({
                title: "Long shift",
                message: `This shift is ${fmtDuration(mins)} long (over 10 hours). Create it anyway?`,
                confirmLabel: "Yes, create",
            });
            if (!ok) return;
        }

        setSaving(true);
        try {
            const url = editId ? `/api/shift/update/${editId}` : "/api/shift/create";
            const method = editId ? "PUT" : "POST";
            const res = await apiFetch(url, {
                method,
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ shift_name: name.trim(), start_time: start, end_time: end }),
                credentials: "include",
            });
            const data = await res.json();
            if (data.success) {
                dnPopup.success(editId ? "Shift Updated" : "Shift Created", `"${name.trim()}" has been saved.`);
                resetForm();
                fetchShifts();
            } else {
                dnPopup.error("Save Failed", data.error || "Something went wrong.");
            }
        } catch (err) {
            console.error("Save shift error:", err);
            dnPopup.error("Error", "Failed to save shift.");
        } finally {
            setSaving(false);
        }
    };

    const editShift = (s) => {
        setEditId(s.shift_id);
        setName(s.shift_name);
        setStart(toHM(s.start_time));
        setEnd(toHM(s.end_time));
        setEndEdited(true); // preserve the saved end; don't auto-clobber on start tweaks
    };

    const deleteShift = async (s) => {
        const ok = await dnPopup.confirm({
            title: "Delete shift?",
            message: `Delete "${s.shift_name}"? Employees assigned to it will need a new shift.`,
            confirmLabel: "Delete",
            danger: true,
        });
        if (!ok) return;
        try {
            const res = await apiFetch(`/api/shift/delete/${s.shift_id}`, { method: "DELETE", credentials: "include" });
            const data = await res.json();
            if (data.success) {
                dnPopup.success("Deleted", "The shift has been removed.");
                if (editId === s.shift_id) resetForm();
                fetchShifts();
            } else {
                dnPopup.error("Delete Failed", data.error || "Something went wrong.");
            }
        } catch (err) {
            console.error("Delete shift error:", err);
            dnPopup.error("Error", "Failed to delete shift.");
        }
    };

    return (
        <div className="shift-container">
            <div className={`shift-dashboard ${navSize}`}>
                <div className="shift-header">
                    <h2>
                        <a className="navbar-brand" href="#">
                            <img src={logo_eagle} alt="dTime Logo" className="page-logo" />
                        </a>
                        Shifts
                    </h2>
                    <p className="shift-subtitle">
                        Create work shifts with start and end times. The end time auto-suggests an 8-hour
                        shift — edit it as needed. Shifts over 10 hours warn; over 12 hours are not allowed.
                    </p>
                </div>

                {/* Create / edit form */}
                <div className="shift-form-card">
                    <div className="shift-format-toggle">
                        <span>Time format:</span>
                        <button
                            type="button"
                            className={timeFormat === "12h" ? "active" : ""}
                            onClick={() => setTimeFormat("12h")}
                        >
                            12-hour (AM/PM)
                        </button>
                        <button
                            type="button"
                            className={timeFormat === "24h" ? "active" : ""}
                            onClick={() => setTimeFormat("24h")}
                        >
                            24-hour
                        </button>
                    </div>
                    <div className="shift-form-grid">
                        <div className="shift-field">
                            <label>Shift Name</label>
                            <input
                                type="text"
                                value={name}
                                placeholder="e.g. General Shift"
                                onChange={(e) => setName(e.target.value)}
                            />
                        </div>
                        <div className="shift-field">
                            <label>Start Time</label>
                            <TimeSelect value={start} format={timeFormat} onChange={onStartChange} />
                        </div>
                        <div className="shift-field">
                            <label>End Time</label>
                            <TimeSelect value={end} format={timeFormat} onChange={onEndChange} />
                        </div>
                        <div className="shift-field shift-duration-field">
                            <label>Duration</label>
                            <div className={`shift-duration${mins != null && mins > WARN_MINS ? " shift-duration-warn" : ""}`}>
                                {mins != null ? fmtDuration(mins) : "—"}
                                {mins != null && mins > MAX_MINS && <span className="shift-dur-note"> · too long</span>}
                            </div>
                        </div>
                        <div className="shift-field shift-actions-field">
                            <button className="shift-save-btn" onClick={save} disabled={saving}>
                                {saving ? "Saving…" : editId ? "Update Shift" : "Create Shift"}
                            </button>
                            {editId && (
                                <button className="shift-cancel-btn" onClick={resetForm} disabled={saving}>
                                    Cancel
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Shift list */}
                <div className="shift-card">
                    {loading ? (
                        <div className="shift-empty">Loading…</div>
                    ) : shifts.length === 0 ? (
                        <div className="shift-empty">No shifts created yet. Add your first shift above.</div>
                    ) : (
                        <table className="shift-table">
                            <thead>
                                <tr>
                                    <th>Shift Name</th>
                                    <th>Start</th>
                                    <th>End</th>
                                    <th>Duration</th>
                                    <th className="shift-actions-col">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {shifts.map((s) => {
                                    const m = durationMins(s.start_time, s.end_time);
                                    return (
                                        <tr key={s.shift_id}>
                                            <td className="shift-name-cell">
                                                {s.shift_name}
                                                {Number(s.is_overnight) === 1 && (
                                                    <span className="shift-overnight" title="Crosses midnight">
                                                        <FaMoon /> overnight
                                                    </span>
                                                )}
                                            </td>
                                            <td>{to12h(s.start_time)}</td>
                                            <td>{to12h(s.end_time)}</td>
                                            <td className={m > WARN_MINS ? "shift-duration-warn" : ""}>{fmtDuration(m)}</td>
                                            <td className="shift-actions-col">
                                                <button className="shift-icon-btn shift-edit" title="Edit" onClick={() => editShift(s)}>
                                                    <FaRegEdit />
                                                </button>
                                                <button className="shift-icon-btn shift-delete" title="Delete" onClick={() => deleteShift(s)}>
                                                    <FaTrashAlt />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Shift;
